import { TypedEventEmitter } from '../utils/TypedEventEmitter';
import type { ConnectionMetrics } from '../stats';
import { createAdaptiveQualityConfig, profileIndex } from './profiles';
import type {
  AdaptiveQualityConfig,
  AdaptiveQualityEvents,
  AveragedConnectionMetrics,
  QualityAction,
  QualityDecision,
  QualityDecisionReason,
  QualityEvaluation,
  QualityMetricsWindow,
  QualityProfile,
  QualityProfileName,
  QualityTarget,
} from './types';

class RollingMetricsWindow implements QualityMetricsWindow {
  private readonly samples: ConnectionMetrics[] = [];

  constructor(private readonly maximumSize: number) {}

  push(metrics: ConnectionMetrics): void {
    this.samples.push(metrics);
    if (this.samples.length > this.maximumSize) this.samples.shift();
  }

  clear(): void {
    this.samples.length = 0;
  }

  values(): readonly ConnectionMetrics[] {
    return this.samples;
  }
}

interface Pressure {
  readonly degraded: boolean;
  readonly severe: boolean;
  readonly cpuLimited: boolean;
  readonly healthy: boolean;
  readonly reason: QualityDecisionReason;
  readonly capacityBps: number | null;
}

export class AdaptiveQualityController extends TypedEventEmitter<AdaptiveQualityEvents> {
  readonly config: Readonly<AdaptiveQualityConfig>;

  private readonly window: QualityMetricsWindow;
  private currentProfileIndex: number;
  private currentBitrateBps: number;
  private currentFramesPerSecond: number;
  private degradedSampleStreak = 0;
  private healthySampleStreak = 0;
  private lastChangeAt = Number.NEGATIVE_INFINITY;

  constructor(config: Partial<AdaptiveQualityConfig> = {}) {
    super();
    this.config = createAdaptiveQualityConfig(config);
    this.window = new RollingMetricsWindow(this.config.movingAverageWindowSize);
    this.currentProfileIndex = profileIndex(this.config, this.config.initialProfile);
    const profile = this.currentProfile;
    this.currentBitrateBps = profile.maxBitrateBps;
    this.currentFramesPerSecond = profile.maxFramesPerSecond;
  }

  get target(): QualityTarget {
    return this.createTarget();
  }

  observe(metrics: ConnectionMetrics): QualityEvaluation {
    this.window.push(metrics);
    const averaged = averageMetrics(this.window.values());

    const pathDecision = this.applyPathPolicy(averaged, metrics.collectedAt);
    if (pathDecision) return this.publishEvaluation(averaged, pathDecision);

    if (averaged.sampleCount < this.config.minimumSamples) {
      return this.publishEvaluation(averaged, null);
    }

    const pressure = this.classifyPressure(averaged);
    if (pressure.degraded || pressure.cpuLimited) {
      this.degradedSampleStreak += 1;
      this.healthySampleStreak = 0;
    } else if (pressure.healthy) {
      this.healthySampleStreak += 1;
      this.degradedSampleStreak = 0;
    } else {
      this.degradedSampleStreak = 0;
      this.healthySampleStreak = 0;
    }

    let decision: QualityDecision | null = null;
    const degradeThreshold = pressure.severe
      ? this.config.severeDegradeConsecutiveSamples
      : this.config.degradeConsecutiveSamples;
    if (
      (pressure.degraded || pressure.cpuLimited) &&
      this.degradedSampleStreak >= degradeThreshold &&
      metrics.collectedAt - this.lastChangeAt >= this.config.degradeCooldownMs
    ) {
      decision = this.degrade(pressure, averaged, metrics.collectedAt);
    } else if (
      pressure.healthy &&
      this.healthySampleStreak >= this.config.upgradeConsecutiveSamples &&
      metrics.collectedAt - this.lastChangeAt >= this.config.upgradeCooldownMs
    ) {
      decision = this.recover(pressure.capacityBps, averaged, metrics.collectedAt);
    }

    if (decision) {
      this.lastChangeAt = metrics.collectedAt;
      this.degradedSampleStreak = 0;
      this.healthySampleStreak = 0;
    }
    return this.publishEvaluation(averaged, decision);
  }

  reset(profileName: QualityProfileName = this.config.initialProfile): QualityTarget {
    this.currentProfileIndex = profileIndex(this.config, profileName);
    const profile = this.currentProfile;
    this.currentBitrateBps = profile.maxBitrateBps;
    this.currentFramesPerSecond = profile.maxFramesPerSecond;
    this.degradedSampleStreak = 0;
    this.healthySampleStreak = 0;
    this.lastChangeAt = Number.NEGATIVE_INFINITY;
    this.window.clear();
    return this.createTarget();
  }

  private get currentProfile(): QualityProfile {
    const profile = this.config.profiles[this.currentProfileIndex];
    if (!profile) throw new Error('adaptive quality profile state is invalid');
    return profile;
  }

  private classifyPressure(metrics: AveragedConnectionMetrics): Pressure {
    const capacityBps = metrics.availableOutgoingBitrateBps ?? metrics.outboundBitrateBps;
    const bandwidthPressure =
      metrics.bandwidthLimitedRatio >= this.config.bandwidthLimitedSampleRatio ||
      (capacityBps !== null &&
        capacityBps < this.currentBitrateBps * this.config.bandwidthDegradeRatio);
    const lossPressure =
      metrics.packetLossPercentage !== null &&
      metrics.packetLossPercentage >= this.config.packetLossDegradePercentage;
    const rttPressure = metrics.rttMs !== null && metrics.rttMs >= this.config.rttDegradeMs;
    const cpuLimited = metrics.cpuLimitedRatio >= this.config.cpuLimitedSampleRatio;
    const severe =
      (capacityBps !== null &&
        capacityBps < this.currentBitrateBps * this.config.bandwidthSevereRatio) ||
      (metrics.packetLossPercentage !== null &&
        metrics.packetLossPercentage >= this.config.packetLossSeverePercentage) ||
      (metrics.rttMs !== null && metrics.rttMs >= this.config.rttSevereMs);

    const betterProfile = this.config.profiles[this.currentProfileIndex - 1];
    const desiredCapacity = betterProfile
      ? betterProfile.upgradeAvailableBitrateBps * this.pathUpgradeMultiplier(metrics)
      : this.effectiveMaximumBitrate(this.currentProfile, metrics);
    const capacityHealthy =
      capacityBps !== null && capacityBps >= desiredCapacity * this.config.upgradeHeadroomRatio;
    const lossHealthy =
      metrics.packetLossPercentage === null ||
      metrics.packetLossPercentage <= this.config.packetLossRecoveryPercentage;
    const rttHealthy = metrics.rttMs === null || metrics.rttMs <= this.config.rttRecoveryMs;
    const limitationHealthy =
      metrics.cpuLimitedRatio < this.config.cpuLimitedSampleRatio &&
      metrics.bandwidthLimitedRatio < this.config.bandwidthLimitedSampleRatio;

    let reason: QualityDecisionReason = 'bandwidth';
    if (lossPressure) reason = 'packet-loss';
    else if (rttPressure) reason = 'rtt';
    else if (cpuLimited && !bandwidthPressure) reason = 'cpu';

    return Object.freeze({
      degraded: bandwidthPressure || lossPressure || rttPressure,
      severe,
      cpuLimited,
      healthy:
        !bandwidthPressure &&
        !lossPressure &&
        !rttPressure &&
        capacityHealthy &&
        lossHealthy &&
        rttHealthy &&
        limitationHealthy,
      reason,
      capacityBps,
    });
  }

  private degrade(
    pressure: Pressure,
    metrics: AveragedConnectionMetrics,
    decidedAt: number,
  ): QualityDecision | null {
    const profile = this.currentProfile;

    // CPU pressure is an encoder-work problem; reducing network bitrate first would not relieve it.
    const cpuOnly = pressure.cpuLimited && !pressure.degraded;
    if (!cpuOnly) {
      const safeBitrate = Math.max(
        profile.minBitrateBps,
        Math.min(
          this.currentBitrateBps * this.config.degradationBitrateReductionRatio,
          (pressure.capacityBps ?? this.currentBitrateBps) * this.config.bitrateUtilizationRatio,
        ),
      );
      if (safeBitrate <= this.currentBitrateBps * (1 - this.config.minimumBitrateChangeRatio)) {
        this.currentBitrateBps = Math.round(safeBitrate);
        return this.createDecision('reduce-bitrate', pressure.reason, decidedAt);
      }
    }

    if (this.currentFramesPerSecond > profile.reducedFramesPerSecond) {
      this.currentFramesPerSecond = profile.reducedFramesPerSecond;
      return this.createDecision('reduce-framerate', cpuOnly ? 'cpu' : pressure.reason, decidedAt);
    }

    if (this.currentProfileIndex < this.config.profiles.length - 1) {
      this.currentProfileIndex += 1;
      const nextProfile = this.currentProfile;
      this.currentBitrateBps = Math.max(
        nextProfile.minBitrateBps,
        Math.min(
          nextProfile.maxBitrateBps,
          this.currentBitrateBps,
          (pressure.capacityBps ?? nextProfile.maxBitrateBps) * this.config.bitrateUtilizationRatio,
        ),
      );
      this.currentFramesPerSecond = Math.min(
        this.currentFramesPerSecond,
        nextProfile.reducedFramesPerSecond,
      );
      return this.createDecision('reduce-resolution', cpuOnly ? 'cpu' : pressure.reason, decidedAt);
    }

    const floor = this.effectiveMinimumBitrate(profile, metrics);
    if (this.currentBitrateBps > floor) {
      this.currentBitrateBps = floor;
      return this.createDecision('reduce-bitrate', pressure.reason, decidedAt);
    }
    return null;
  }

  private recover(
    capacityBps: number | null,
    metrics: AveragedConnectionMetrics,
    decidedAt: number,
  ): QualityDecision | null {
    if (capacityBps === null) return null;
    const profile = this.currentProfile;
    const maximumBitrate = this.effectiveMaximumBitrate(profile, metrics);
    const conservativeBitrate = Math.min(
      maximumBitrate,
      capacityBps / this.config.upgradeHeadroomRatio,
    );
    if (
      conservativeBitrate >=
      this.currentBitrateBps * (1 + this.config.minimumBitrateChangeRatio)
    ) {
      this.currentBitrateBps = Math.round(conservativeBitrate);
      return this.createDecision('increase-bitrate', 'recovery', decidedAt);
    }

    const ceilingIndex = this.pathCeilingIndex(metrics);
    const betterProfile = this.config.profiles[this.currentProfileIndex - 1];
    if (betterProfile && this.currentProfileIndex - 1 >= ceilingIndex) {
      const required =
        betterProfile.upgradeAvailableBitrateBps *
        this.pathUpgradeMultiplier(metrics) *
        this.config.upgradeHeadroomRatio;
      if (capacityBps >= required) {
        this.currentProfileIndex -= 1;
        this.currentBitrateBps = Math.round(
          Math.max(
            betterProfile.minBitrateBps,
            Math.min(
              this.effectiveMaximumBitrate(betterProfile, metrics),
              capacityBps / this.config.upgradeHeadroomRatio,
            ),
          ),
        );
        this.currentFramesPerSecond = Math.min(
          this.currentFramesPerSecond,
          betterProfile.maxFramesPerSecond,
        );
        return this.createDecision('increase-resolution', 'recovery', decidedAt);
      }
    }

    if (this.currentFramesPerSecond < profile.maxFramesPerSecond) {
      this.currentFramesPerSecond = profile.maxFramesPerSecond;
      return this.createDecision('increase-framerate', 'recovery', decidedAt);
    }
    return null;
  }

  private applyPathPolicy(
    metrics: AveragedConnectionMetrics,
    decidedAt: number,
  ): QualityDecision | null {
    const ceilingIndex = this.pathCeilingIndex(metrics);
    if (this.currentProfileIndex < ceilingIndex) {
      this.currentProfileIndex = ceilingIndex;
      const profile = this.currentProfile;
      this.currentBitrateBps = this.effectiveMaximumBitrate(profile, metrics);
      this.currentFramesPerSecond = Math.min(
        this.currentFramesPerSecond,
        profile.maxFramesPerSecond,
      );
      this.recordPathDecision(decidedAt);
      return this.createDecision('path-policy', 'path-policy', decidedAt);
    }

    const maximumBitrate = this.effectiveMaximumBitrate(this.currentProfile, metrics);
    if (this.currentBitrateBps > maximumBitrate) {
      this.currentBitrateBps = maximumBitrate;
      this.recordPathDecision(decidedAt);
      return this.createDecision('path-policy', 'path-policy', decidedAt);
    }
    return null;
  }

  private recordPathDecision(decidedAt: number): void {
    this.lastChangeAt = decidedAt;
    this.degradedSampleStreak = 0;
    this.healthySampleStreak = 0;
  }

  private pathCeilingIndex(metrics: AveragedConnectionMetrics): number {
    if (metrics.connectionPath === 'relay') {
      return profileIndex(this.config, this.config.turnMaximumProfile);
    }
    if (metrics.connectionPath === 'unknown') {
      return profileIndex(this.config, this.config.unknownPathMaximumProfile);
    }
    return 0;
  }

  private pathUpgradeMultiplier(metrics: AveragedConnectionMetrics): number {
    if (metrics.connectionPath === 'relay') return this.config.turnUpgradeThresholdMultiplier;
    if (metrics.lanLike) return this.config.lanUpgradeThresholdMultiplier;
    return 1;
  }

  private effectiveMaximumBitrate(
    profile: QualityProfile,
    metrics: AveragedConnectionMetrics,
  ): number {
    const multiplier =
      metrics.connectionPath === 'relay'
        ? this.config.turnBitrateMultiplier
        : metrics.lanLike
          ? this.config.lanBitrateMultiplier
          : 1;
    return Math.round(profile.maxBitrateBps * multiplier);
  }

  private effectiveMinimumBitrate(
    profile: QualityProfile,
    metrics: AveragedConnectionMetrics,
  ): number {
    if (metrics.connectionPath !== 'relay') return profile.minBitrateBps;
    return Math.round(profile.minBitrateBps * this.config.turnBitrateMultiplier);
  }

  private createTarget(): QualityTarget {
    const profile = this.currentProfile;
    return Object.freeze({
      profile: profile.name,
      maxWidth: profile.maxWidth,
      maxHeight: profile.maxHeight,
      maxBitrateBps: Math.round(this.currentBitrateBps),
      maxFramesPerSecond: this.currentFramesPerSecond,
    });
  }

  private createDecision(
    action: QualityAction,
    reason: QualityDecisionReason,
    decidedAt: number,
  ): QualityDecision {
    return Object.freeze({ ...this.createTarget(), action, reason, decidedAt });
  }

  private publishEvaluation(
    metrics: AveragedConnectionMetrics,
    decision: QualityDecision | null,
  ): QualityEvaluation {
    const evaluation = Object.freeze({
      metrics,
      target: this.createTarget(),
      decision,
      degradedSampleStreak: this.degradedSampleStreak,
      healthySampleStreak: this.healthySampleStreak,
    });
    if (decision) this.emit('decision', { decision });
    this.emit('evaluation', { evaluation });
    return evaluation;
  }
}

function averageMetrics(samples: readonly ConnectionMetrics[]): AveragedConnectionMetrics {
  const latest = samples[samples.length - 1];
  const connectionPath = latest?.connectionPath ?? 'unknown';
  const lanLike =
    connectionPath === 'direct' &&
    latest?.localCandidateType === 'host' &&
    latest.remoteCandidateType === 'host';
  return Object.freeze({
    sampleCount: samples.length,
    availableOutgoingBitrateBps: average(samples, 'availableOutgoingBitrateBps'),
    outboundBitrateBps: average(samples, 'outboundBitrateBps'),
    packetLossPercentage: average(samples, 'packetLossPercentage'),
    rttMs: average(samples, 'rttMs'),
    cpuLimitedRatio: limitationRatio(samples, 'cpu'),
    bandwidthLimitedRatio: limitationRatio(samples, 'bandwidth'),
    connectionPath,
    lanLike,
  });
}

function average(
  samples: readonly ConnectionMetrics[],
  key: 'availableOutgoingBitrateBps' | 'outboundBitrateBps' | 'packetLossPercentage' | 'rttMs',
): number | null {
  const values = samples
    .map((sample) => sample[key])
    .filter((value): value is number => value !== null && Number.isFinite(value));
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function limitationRatio(
  samples: readonly ConnectionMetrics[],
  reason: 'cpu' | 'bandwidth',
): number {
  if (samples.length === 0) return 0;
  return (
    samples.filter((sample) => sample.qualityLimitationReason === reason).length / samples.length
  );
}
